import { useState } from 'react';
import {
  Card,
  CardDescription,
  CardTitle,
  EmptyState,
  ErrorState,
  NativeSelect,
  Skeleton,
  SurvivalChart,
} from '@mgdlead/ui';
import { UserMinus } from 'lucide-react';

import { useWorkspace } from '../workspace/WorkspaceContext';
import {
  useCampaigns,
  useRetentionCurve,
  type CampaignDetailData,
  type PeriodArg,
  type RetentionCurveSeries,
  type RetentionRecorte,
} from '../../lib/api';
import { TabelaDeQuebra, TabelaPorMarco } from './RetentionTables';
import { TOOLTIP_D1_X_24H } from './retention-ui';

/**
 * A seção "Retenção" da página da campanha (Curva de Evasão, ADR-0019): a curva do bloco ao
 * lado da do workspace (ou de até 2 outras campanhas, por "Comparar com…"), a tabela por
 * marco e as quebras por anúncio, campanha da Meta e origem — tudo pronto do servidor.
 */
export function RetencaoDaCampanha({
  data,
  campaignId,
  periodo,
}: {
  data: CampaignDetailData['retencao'];
  campaignId: string;
  periodo: PeriodArg;
}) {
  const { workspace } = useWorkspace();
  const campanhas = useCampaigns(workspace.id);
  const [comparar, setComparar] = useState<string>('');

  const series: RetentionRecorte[] = comparar
    ? [
        { kind: 'CAMPAIGN', id: campaignId },
        { kind: 'CAMPAIGN', id: comparar },
        { kind: 'WORKSPACE' },
      ]
    : [];
  const comparacao = useRetentionCurve(workspace.id, periodo, series, comparar !== '');
  const curva: readonly RetentionCurveSeries[] = comparar
    ? (comparacao.data?.series ?? [])
    : data.curva;
  const bloco = data.curva[0];

  const opcoes = (campanhas.data?.campaigns ?? [])
    .filter((c) => c.id !== campaignId)
    .map((c) => ({ value: c.id, label: c.name }));

  return (
    <section className="flex flex-col gap-3" aria-labelledby="retencao-titulo">
      <h2 id="retencao-titulo" className="text-lg font-semibold text-foreground">
        Retenção
      </h2>

      <Card padded={false} className="p-3 md:p-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>Quanto do tráfego continua no grupo</CardTitle>
            <CardDescription>
              Só conta quem saiu por conta própria; quem você removeu fica à parte.{' '}
              {TOOLTIP_D1_X_24H}
            </CardDescription>
          </div>
          <label className="flex min-w-56 flex-col gap-1 text-xs text-muted-foreground">
            Comparar com…
            <NativeSelect
              size="sm"
              value={comparar}
              onChange={(e) => setComparar(e.target.value)}
              options={[{ value: '', label: 'Todo o workspace' }, ...opcoes]}
            />
          </label>
        </div>

        <div className="mt-3">
          {comparar && comparacao.isError ? (
            <ErrorState
              title="Não foi possível comparar"
              description={comparacao.error.message}
              onRetry={() => void comparacao.refetch()}
            />
          ) : comparar && comparacao.isPending ? (
            <Skeleton className="h-56 w-full" />
          ) : !bloco || bloco.tamanho === 0 ? (
            <EmptyState
              kind="no-data"
              icon={UserMinus}
              title="Nenhuma entrada nos grupos desta campanha no período"
              description="Assim que alguém entrar, mostramos aqui quantos saem com o tempo."
              compact
            />
          ) : (
            <SurvivalChart
              ariaLabel="Curva de sobrevivência: porcentagem que continua no grupo em cada marco"
              milestones={bloco.pontos.map((p) => p.marco)}
              series={curva.map((s) => ({
                id: JSON.stringify(s.recorte),
                label: s.rotulo,
                n: s.tamanho,
                values: s.pontos.map((p) => p.ficaram.valor),
                dashed: s.recorte.kind === 'ORIGIN' && s.recorte.origin === 'SEM_ATRIBUICAO',
              }))}
            />
          )}
        </div>
      </Card>

      {bloco && bloco.tamanho > 0 ? (
        <Card padded={false} className="p-3 md:p-4">
          <CardTitle>Por marco</CardTitle>
          <CardDescription>As quatro colunas fecham 100% de cada marco vencido.</CardDescription>
          <div className="mt-3">
            <TabelaPorMarco serie={bloco} />
          </div>
        </Card>
      ) : null}

      {data.porAnuncio.length > 0 ? (
        <Card padded={false} className="p-3 md:p-4">
          <CardTitle>Por anúncio</CardTitle>
          <CardDescription>
            O termômetro do criativo: anúncio barato com evasão alta no 1º dia é público ou criativo
            errado. Ordenado por custo por pessoa que ficou 7 dias.
          </CardDescription>
          <div className="mt-3">
            <TabelaDeQuebra
              rows={data.porAnuncio}
              caption="Evasão por anúncio"
              primeiraColuna="Anúncio"
              comCusto
            />
          </div>
        </Card>
      ) : null}

      {data.porCampanhaMeta.length > 0 ? (
        <Card padded={false} className="p-3 md:p-4">
          <CardTitle>Por campanha da Meta</CardTitle>
          <div className="mt-3">
            <TabelaDeQuebra
              rows={data.porCampanhaMeta}
              caption="Evasão por campanha da Meta"
              primeiraColuna="Campanha da Meta"
              comCusto
            />
          </div>
        </Card>
      ) : null}

      {data.porOrigem.length > 0 ? (
        <Card padded={false} className="p-3 md:p-4">
          <CardTitle>Por origem</CardTitle>
          <CardDescription>
            &ldquo;Sem atribuição&rdquo; é entrada sem clique casado — origem desconhecida, não
            orgânico.
          </CardDescription>
          <div className="mt-3">
            <TabelaDeQuebra
              rows={data.porOrigem}
              caption="Evasão por origem"
              primeiraColuna="Origem"
              comCusto={false}
            />
          </div>
        </Card>
      ) : null}
    </section>
  );
}
